import React from 'react'
import { technicianBookingSteps } from 'data/service-hub-content'

// "How to book" steps, shared by every service hub. Step copy lives in
// data/service-hub-content.js. Steps are numbered here, so titles don't include the number.
//
// theme "dark": slate band, used by the handyman, electrician, plumber and
//   part-time helper pages. It replaced a version with fixed h-[1300px] heights;
//   this one sizes to its content.
// theme "light": white cards with coloured icon tiles (step.color), used by the aircon pages.
//
// `headingLevel` lets a page nest this under another h2 (the step titles
// drop one level to match).
export const HowToBook = ({
    serviceLabel = 'handyman',
    title,
    steps,
    theme = 'dark',
    headingLevel = 'h2',
}) => {
    const Heading = headingLevel
    const StepHeading = headingLevel === 'h2' ? 'h3' : 'h4'
    const list = steps || technicianBookingSteps(serviceLabel)
    const label = serviceLabel.charAt(0).toUpperCase() + serviceLabel.slice(1)
    const heading = title || `How to Book ${/^[aeiou]/i.test(serviceLabel) ? 'an' : 'a'} ${label}`

    if (theme === 'light') {
        return (
            <section id="how-to-book" className="py-8 md:py-12 bg-linear-to-b from-gray-50 to-white" aria-labelledby="how-to-book-heading">
                <div className="max-w-5xl mx-auto px-4">
                    <Heading id="how-to-book-heading" className="text-2xl md:text-3xl font-bold text-gray-900 uppercase text-center mb-5">
                        {heading}
                    </Heading>
                    {/* Compact row layout: icon + number inline with the text instead of
                        stacked and centred, which halves the card height on mobile. */}
                    <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        {list.map(({ icon: Icon, title: stepTitle, desc, color = 'from-orange-500 to-amber-500' }, i) => (
                            <li key={stepTitle}>
                                <article className="flex items-start gap-3 bg-white rounded-2xl p-4 border border-gray-100 shadow-sm h-full">
                                    <div className={`shrink-0 w-9 h-9 rounded-xl bg-linear-to-br ${color} flex items-center justify-center shadow-sm`}>
                                        <Icon className="text-white text-sm" aria-hidden="true" />
                                    </div>
                                    <div className="min-w-0">
                                        <StepHeading className="font-bold text-sm text-gray-800 mb-1">{i + 1}. {stepTitle}</StepHeading>
                                        <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
                                    </div>
                                </article>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>
        )
    }

    return (
        <section id="how-to-book" className="bg-linear-to-br from-slate-700 to-slate-900 text-gray-100 py-10 px-4">
            <div className="max-w-5xl mx-auto">
                <Heading className="text-xl md:text-2xl font-bold text-center uppercase mb-6">
                    {heading}
                </Heading>
                <ol className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {list.map(({ icon: Icon, title: stepTitle, desc }, i) => (
                        <li key={stepTitle} className="bg-white/10 border border-white/15 rounded-2xl p-4">
                            <Icon aria-hidden="true" className="text-2xl text-amber-300 mb-2" />
                            <StepHeading className="text-sm font-bold mb-1">{i + 1}. {stepTitle}</StepHeading>
                            <p className="text-sm text-gray-300 leading-relaxed">{desc}</p>
                        </li>
                    ))}
                </ol>
            </div>
        </section>
    )
}
